/**
 * Issue #860 — overs are overs and balls, not a decimal. `3.4` overs is 3
 * overs and 4 balls: 22 balls, or 3 2/3 overs. Every rate with overs as its
 * divisor works in balls and multiplies by 6; a total of overs is summed in
 * balls and printed back in the over.ball notation players type.
 *
 * Mirrors `api/src/lib/overs.ts`: change both together.
 */
import { counterOrNull } from "./counters";

/** D254 Q8: the words a refused overs value is answered with. Same string as the API's. */
export const OVERS_INVALID_WORDS =
  "Overs are overs and balls: 3.4 is 3 overs and 4 balls. The number after the point goes up to 5.";

/** No spell is longer than this; a bigger figure is a typo, not overs. */
export const MAX_OVERS = 999;

/** N2: a magnitude that cannot be real has its own words. Same string as the API's. */
export const OVERS_TOO_LARGE_WORDS = "Overs looks too large — check the number.";

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * `overs.balls` to a whole number of balls. Valid only for a finite number
 * >= 0 with at most one decimal digit, and that digit 0 to 5. Anything else
 * is invalid and gives `null`.
 */
export function oversToBalls(overs) {
  if (overs === null || overs === undefined || !Number.isFinite(overs) || overs < 0 || overs > MAX_OVERS) return null;
  const whole = Math.trunc(overs);
  const tenths = (overs - whole) * 10;
  const digit = Math.round(tenths);
  if (Math.abs(tenths - digit) > 1e-5 || digit > 5) return null;
  return whole * 6 + digit;
}

/** `null` when the figure is fine; otherwise the words to refuse it with. */
export function oversRefusal(overs) {
  if (overs === null || overs === undefined) return null;
  if (Number.isFinite(overs) && overs > MAX_OVERS) return OVERS_TOO_LARGE_WORDS;
  return oversToBalls(overs) === null ? OVERS_INVALID_WORDS : null;
}

/** Balls back to over.ball notation: 44 -> 7.2, 18 -> 3. */
export function ballsToOvers(balls) {
  const v = Math.floor(balls / 6) + (balls % 6) / 10;
  return Math.round(v * 10) / 10;
}

/** Runs per over (six balls), two decimals; `null` with no balls to divide by. */
export function perOverRate(runs, balls) {
  return balls !== null && balls !== undefined && balls > 0 ? round2((runs * 6) / balls) : null;
}

/**
 * D260: older rows can hold overs that are not over.ball notation (a stored
 * 3.7). They stay out of the overs-based rates, so any line that reports
 * overs beside a rate says how many spells it left out. `null` when none.
 */
export function leftOutNote(n) {
  if (!n || n < 1) return null;
  return `${n === 1 ? "one spell" : `${n} spells`} with unreadable overs left out`;
}

/** The count of spells in a match list whose overs are present but unreadable. */
export function unreadableOversCount(matches) {
  return (matches || []).filter((m) => {
    const o = m?.bowling?.overs;
    return o !== null && o !== undefined && oversToBalls(o) === null;
  }).length;
}

/**
 * The form's economy, from its raw typed strings. Blank runs count as 0 (as
 * `rateOrNull` does); blank, zero or invalid overs give `null`.
 */
export function economyOrNull(runs, overs) {
  const balls = oversToBalls(counterOrNull(overs));
  if (!balls) return null;
  return perOverRate(counterOrNull(runs) ?? 0, balls);
}
